import * as Sentry from "@sentry/react"
import { useMutation } from "@tanstack/react-query"
import { useEffect, useState } from "react"
import {
  Outlet,
  useParams,
  useRouteError,
  useSearchParams,
} from "react-router-dom"
import { getToken } from "@/api/api"
import { useAppStore, useHydrated } from "@/api/app-store"
import { AppError, ExpiredTokenError } from "@/api/errors"
import { ErrorMessage } from "@/components/error-message"
import { PageLoader } from "@/components/page-loader"
import { useIsMounted } from "@/lib/use-is-mounted"

const SessionLayout = () => {
  const isMounted = useIsMounted()
  const hasHydrated = useHydrated()
  const appStore = useAppStore()
  const [tokenReady, setTokenReady] = useState(false)

  const [searchParams, setSearchParams] = useSearchParams()
  const params = useParams<{ token_id: string }>()
  const token_id = params.token_id as string
  const code = searchParams.get("code")

  const currentToken = useAppStore((state) => state.tokens[token_id])

  const mutation = useMutation({
    mutationKey: ["getToken", { token_id, code }],
    mutationFn: getToken,
    onSuccess: (data) => {
      appStore.setToken(token_id, data.token)
      setTokenReady(true)
      setSearchParams()
    },
  })

  useEffect(() => {
    if (isMounted() && code && mutation.isIdle) {
      mutation.mutate({ token_id, code })
    }
  }, [code, isMounted, mutation, token_id])

  useEffect(() => {
    // If token already exists (returning user), mark as ready
    if (currentToken && !tokenReady) {
      setTokenReady(true)
    }
  }, [currentToken, tokenReady])

  if (mutation.isPending) {
    return <PageLoader />
  }

  if (mutation.isError) {
    throw mutation.error
  }

  if (currentToken && tokenReady) {
    return <Outlet />
  }

  // Wait for store hydration before deciding token is missing
  if (!hasHydrated) {
    return <PageLoader />
  }

  if (!code && !currentToken) {
    throw new ExpiredTokenError()
  }

  return <PageLoader />
}

const ErrorBoundary = () => {
  const error = useRouteError()

  // Always report errors to Sentry when error boundary is shown
  useEffect(() => {
    if (error) {
      Sentry.captureException(error, {
        tags: {
          errorBoundary: "SessionLayout",
          errorType: error instanceof AppError ? "AppError" : "UnknownError",
        },
        extra: {
          errorName: error instanceof Error ? error.name : "Unknown",
          errorMessage: error instanceof Error ? error.message : String(error),
          errorStack: error instanceof Error ? error.stack : undefined,
          errorTitle: error instanceof AppError ? error.title : undefined,
          url: window.location.href,
          userAgent: navigator.userAgent,
          timestamp: new Date().toISOString(),
        },
        level: "error",
      })
    }
  }, [error])

  if (!(error instanceof AppError)) {
    return (
      <div className="mx-auto md:w-10/12 lg:w-8/12 xl:w-6/12">
        <ErrorMessage refresh>Error inesperado</ErrorMessage>
      </div>
    )
  }

  return (
    <div className="mx-auto md:w-10/12 lg:w-8/12 xl:w-6/12">
      <ErrorMessage title={error.title}>{error.message}</ErrorMessage>
    </div>
  )
}

export { ErrorBoundary, SessionLayout }
